import React, { useState, useEffect, useRef } from 'react';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useBooking } from '../../contexts/booking/BookingContext';
import { formatPrice } from '../../utils/bookingUtils';
import { supabase, SUPABASE_ANON_KEY } from '../../lib/supabaseClient';
import { edgeFunctionUrl, EdgeFunctions } from '../../lib/edgeFunctions';

// Debug: Check which Stripe key is loaded
console.log('🔑 Stripe Key:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.substring(0, 20) + '...');
console.log('🔑 Key Type:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.includes('test') ? 'TEST' : 'LIVE');

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Inner component that has access to Stripe Elements
const PaymentFormInner: React.FC = () => {
  const { bookingState, prevStep, nextStep, getTotalPrice, setPaymentIntent, setPaymentStatus, saveBookingState } = useBooking();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const stripe = useStripe();
  const elements = useElements();
  const totalAmount = getTotalPrice();
  

  // Handle Pay Later functionality
  const handlePayLater = async () => {
    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error: updateError } = await supabase
        .from('appointments')
        .update({ payment_status: 'pending_payment' })
        .eq('id', bookingState.appointmentId);

      if (updateError) {
        throw new Error(updateError.message);
      }

      setPaymentStatus('pending');
      setPaymentIntent('pay_later');
      nextStep();
    } catch (err: any) {
      console.error('Pay Later error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }
    
    setIsProcessing(true);
    setErrorMessage(null);
    
    try {
      // Save booking state to localStorage before payment (for Afterpay redirects)
      saveBookingState();
      
      // Confirm payment with redirect support for Afterpay
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/booking?step=confirmation&appointment_id=${bookingState.appointmentId}`,
          payment_method_data: {
            billing_details: {
              name: `${bookingState.customerInfo?.firstName} ${bookingState.customerInfo?.lastName}`,
              email: bookingState.customerInfo?.email,
            },
          },
        },
        redirect: 'if_required', // Only redirect for payment methods that require it (like Afterpay)
      });
      
      if (error) {
        // Payment failed or was cancelled
        setErrorMessage(error.message || 'Payment failed');
        setPaymentStatus('failed');
        setIsProcessing(false);
      } else {
        // Payment succeeded without redirect (e.g., card payment)
        setPaymentStatus('succeeded');
        
        // Move to confirmation step (webhook will confirm payment)
        nextStep();
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred');
      setPaymentStatus('failed');
      setIsProcessing(false);
    }
  };
  
  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Payment</h2>
      <p className="text-gray-600 mb-6">Please select your payment method to complete your booking</p>
      
      {/* Payment summary */}
      <div className="bg-[#F1F7F4] p-4 rounded-md mb-6">
        <h3 className="font-medium text-[#232347] mb-2">Payment Summary</h3>
        <div className="flex justify-between font-medium">
          <span>Total Amount:</span>
          <span>${formatPrice(totalAmount)}</span>
        </div>
      </div>
      
      {/* Payment form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-[#232347] mb-2">Payment Method</label>
          <div className="bg-white">
            <PaymentElement />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Your payment information is secure and encrypted.
          </p>
          <p className="text-sm text-[#3A5E63] mt-1">
            💳 Pay with card or split into 4 interest-free payments with Afterpay
          </p>
        </div>
        
        {errorMessage && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
            {errorMessage}
          </div>
        )}

        {/* Information banner for Pay Later option */}
        <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 p-3 rounded-md">
          <p className="text-sm">
            <strong>Payment Options:</strong> You can pay now with a card or choose "Pay Later" to complete payment at the spa.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row justify-between gap-3 mt-8">
          <button
            type="button"
            onClick={prevStep}
            disabled={isProcessing}
            className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50"
          >
            Back
          </button>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handlePayLater}
              disabled={isProcessing}
              className="px-6 py-2 rounded-md border-2 border-[#3A5E63] text-[#3A5E63] hover:bg-[#3A5E63] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-[#3A5E63] border-t-transparent rounded-full mr-2"></span>
                  Processing...
                </>
              ) : (
                'Pay Later'
              )}
            </button>

            <button
              type="submit"
              disabled={!stripe || !elements || isProcessing}
              className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  Processing...
                </>
              ) : (
                'Pay Now'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// Outer component that creates payment intent and wraps with Elements
const PaymentForm: React.FC = () => {
  const { bookingState, getTotalPrice, setPaymentStatus } = useBooking();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const creationInitiatedRef = useRef(false);
  const totalAmount = getTotalPrice();
  
  // Create payment intent on mount
  useEffect(() => {
    const createPaymentIntent = async () => {
      if (creationInitiatedRef.current) {
        return;
      }
      creationInitiatedRef.current = true;

      try {
        setIsLoading(true);

        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        };

        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }

        const response = await fetch(
          edgeFunctionUrl(EdgeFunctions.CREATE_PAYMENT_INTENT),
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              amount: totalAmount,
              appointment_id: bookingState.appointmentId,
              services: bookingState.selectedServices.map(service => ({
                id: service.id,
                name: service.name
              })),
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage;
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error?.message || `Error ${response.status}`;
          } catch (e) {
            errorMessage = `Error ${response.status}: ${errorText}`;
          }
          throw new Error(errorMessage);
        }

        const { clientSecret } = await response.json();
        if (!clientSecret) {
          throw new Error('No client secret returned from server');
        }

        setClientSecret(clientSecret);
        setPaymentStatus('pending');
      } catch (error: any) {
        console.error('Error creating payment intent:', error);
        setError(error.message || 'Failed to initialize payment');
      } finally {
        setIsLoading(false);
      }
    };

    if (bookingState.appointmentId) {
      createPaymentIntent();
    } else {
      setError('Booking information is incomplete. Please go back and review your booking details.');
      setIsLoading(false);
    }
  }, [bookingState.appointmentId, bookingState.selectedServices, setPaymentStatus, totalAmount]);
  
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-12 w-12 border-4 border-[#B2D8C5] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">Initializing payment...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (!clientSecret) {
    return null;
  }
  
  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#B2D8C5',
      },
    },
  };
  
  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentFormInner />
    </Elements>
  );
};

export default PaymentForm;